import type React from 'react';
import type { CollectionCardActionBinding } from '../../lib/collectionCardActions';
import type {
  CollectionCardTemplateImageAspect,
  CollectionCardTemplateRowType,
} from '../../lib/collectionCardTemplates';

export function getCollectionCardImageAspectClass(aspect: CollectionCardTemplateImageAspect) {
  switch (aspect) {
    case 'portrait-3-4':
      return '3 / 4';
    case 'portrait':
      return '4 / 5';
    case 'landscape':
      return '16 / 10';
    case 'original':
      return null;
    case 'square':
    default:
      return '1 / 1';
  }
}

export function resolveCollectionCardImageFrameStyle(
  aspect: CollectionCardTemplateImageAspect
): React.CSSProperties {
  const aspectRatio = getCollectionCardImageAspectClass(aspect);
  return aspectRatio ? { aspectRatio } : {};
}

export function resolveCollectionActionTargetModelId(
  action: CollectionCardActionBinding,
  fallbackModelReference: number
) {
  if (action.action !== 'selectVase') return fallbackModelReference;
  if (action.useTargetOverride !== true) return fallbackModelReference;
  const parsedTarget = Number(action.target);
  return Number.isFinite(parsedTarget) && parsedTarget > 0 ? parsedTarget : fallbackModelReference;
}

export function hexToRgba(color: string | undefined, opacity: number) {
  if (!color) return `rgba(15, 23, 42, ${opacity})`;
  const normalized = color.trim();
  const hex = normalized.startsWith('#') ? normalized.slice(1) : normalized;
  if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/i.test(hex)) return normalized;
  const expanded = hex.length === 3 ? hex.split('').map((part) => `${part}${part}`).join('') : hex;
  const r = parseInt(expanded.slice(0, 2), 16);
  const g = parseInt(expanded.slice(2, 4), 16);
  const b = parseInt(expanded.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${Math.max(0, Math.min(1, opacity))})`;
}

export function collectionCardShadowToCss(template: {
  cardShadowColour: string;
  cardShadowOpacity: number;
  cardShadowX: number;
  cardShadowY: number;
  cardShadowBlur: number;
  cardShadowSpread: number;
}) {
  if (
    template.cardShadowX === 0 &&
    template.cardShadowY === 0 &&
    template.cardShadowBlur === 0 &&
    template.cardShadowSpread === 0 &&
    template.cardShadowOpacity <= 0
  ) {
    return undefined;
  }
  return `${template.cardShadowX}px ${template.cardShadowY}px ${template.cardShadowBlur}px ${template.cardShadowSpread}px ${hexToRgba(
    template.cardShadowColour,
    template.cardShadowOpacity
  )}`;
}

export function resolveCollectionCardRowGap(
  previousRow: CollectionCardTemplateRowType | null,
  currentRow: CollectionCardTemplateRowType,
  template: {
    imageTextGap: number;
    titleDescriptionGap: number;
  }
) {
  if (!previousRow) return 0;
  if (previousRow === 'image') return template.imageTextGap;
  if (
    (previousRow === 'title' && currentRow === 'description') ||
    (previousRow === 'description' && currentRow === 'title')
  ) {
    return template.titleDescriptionGap;
  }
  return template.titleDescriptionGap;
}
